import React, { useContext } from 'react'
import cls from 'classnames';
import styles from './horizontal_field.module.scss';
import { ViewSectionContext } from '../ViewSectionList/ViewSectionList';

export interface HorizontalFieldProps {
  label: React.ReactNode;
  description?: React.ReactNode;
  containerStyle?: React.CSSProperties;
  containerClassName?: string;
  labelContainerStyle?: React.CSSProperties;
  labelContainerClassName?: string;

  /**
   * Sets ARIA attributes and controls whether the label can be clicked.
   */
  disabled?: boolean;

  /**
   * Positions the component before the label.
   */
  before?: boolean;

  children?: any;
}

/**
 * A field with a label and component laid out in a row.
 * @author Johan Svensson
 */
export default function HorizontalField({ label, before, disabled, children, ...props }: HorizontalFieldProps) {
  const childrenContainerRef = React.useRef<HTMLElement>(null);

  const viewSectionContext = useContext(ViewSectionContext);

  function onLabelClick() {
    const child = childrenContainerRef.current?.children[0] as HTMLElement;
    child?.click();
  }

  if (typeof before !== 'boolean' && viewSectionContext) {
    before = true;
  }

  return (
    <div
      style={props.containerStyle}
      className={cls([styles.container, props.containerClassName, before ? styles.before : null])}
      aria-disabled={disabled}>
      {
        before ? (<div ref={e => childrenContainerRef.current = e} style={{ display: 'inline-flex' }}>{children}</div>) : null
      }

      <div
        style={props.labelContainerStyle}
        className={cls([
          styles.labelContainer,
          props.labelContainerClassName,
          disabled ? 'disabled' : null
        ])}>
        <label
          role="button"
          className="label"
          onClick={!disabled ? onLabelClick : null}>
          {label}
        </label>
        {
          props.description ? (
            <p><small className={cls([styles.subtitle, "subtitle"])}>{props.description}</small></p>
          ) : null
        }
      </div>

      {
        !before ? (<div ref={e => childrenContainerRef.current = e} style={{ display: 'flex' }}>{children}</div>) : null
      }
    </div>
  )
}